/**
 * Stock Detail Page - คงเหลือแยกตามคลัง (ซ้าย) + ประวัติสินค้าเคลื่อนไหว (ขวา)
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Input,
  Select,
  Row,
  Col,
  Typography,
  Table,
  Button,
  Space,
  message,
} from "antd";
import { SearchOutlined, EditOutlined, ArrowLeftOutlined, CopyOutlined } from "@ant-design/icons";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import AdjustWhModal from "../../components/Modal/AdjustWhModal";
import Pagination from "../../components/Pagination";
import { getDataWithComp, parseCurrency } from "../../../utils/SamuiUtils";
import api from "../../../utils/axios";

const { Title } = Typography;

const formatTH = (v) => {
  if (!v) return "-";
  // mssql ส่ง datetime พร้อม Z แต่ค่าเป็นเวลาไทยอยู่แล้ว → ดึง literal ออกโดยไม่ผ่าน Date
  const m = String(v).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return String(v);
  const [, yr, mo, dy, hh, mm, ss] = m;
  return `${dy}/${mo}/${Number(yr) + 543} ${hh}:${mm}:${ss}`;
};

const MONTH_OPTIONS = [
  { value: "", label: "ทั้งปี" },
  { value: "01", label: "มกราคม" },
  { value: "02", label: "กุมภาพันธ์" },
  { value: "03", label: "มีนาคม" },
  { value: "04", label: "เมษายน" },
  { value: "05", label: "พฤษภาคม" },
  { value: "06", label: "มิถุนายน" },
  { value: "07", label: "กรกฎาคม" },
  { value: "08", label: "สิงหาคม" },
  { value: "09", label: "กันยายน" },
  { value: "10", label: "ตุลาคม" },
  { value: "11", label: "พฤศจิกายน" },
  { value: "12", label: "ธันวาคม" },
];

const currentYearBE = new Date().getFullYear() + 543;
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) =>
  (currentYearBE - i).toString()
).map((y) => ({ value: y, label: y }));

const getContractCustomerName = (row) =>
  row?.Customer_Name ||
  row?.customer_name ||
  row?.Debtor_Name ||
  row?.debtor_name ||
  row?.AR_Name ||
  row?.ar_name ||
  row?.Contract_Customer_Name ||
  row?.contract_customer_name ||
  "-";

export default function StockDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPage = location.state?.from || "/stock/stock-overview";
  const fromLabel =
    location.state?.fromLabel ||
    (fromPage === "/stock/stock-documents" ? "เอกสารงานคลัง" : "ดูสต๊อกสินค้า");
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ---- Left data (hidden, used to resolve selectedRow from URL) ----
  const [onhandRows, setOnhandRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  // ---- Right: ประวัติสินค้าเคลื่อนไหว ----
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histYear, setHistYear] = useState(currentYearBE.toString());
  const [histMonth, setHistMonth] = useState("");
  const [histSearch, setHistSearch] = useState("");
  const [histSearchInput, setHistSearchInput] = useState("");
  const [empMap, setEmpMap] = useState({});
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const histContainerRef = useRef(null);
  const [histScrollY, setHistScrollY] = useState(400);
  const [histPage, setHistPage] = useState(1);
  const [histPageSize, setHistPageSize] = useState(100);

  const [itemTotalOnhand, setItemTotalOnhand] = useState(null);
  const [contractCustomerMap, setContractCustomerMap] = useState({});
  const [tempEquipGroupIds, setTempEquipGroupIds] = useState(new Set());
  const [isTempEquipItem, setIsTempEquipItem] = useState(false);

  // ---- Shared ----
  const [whDataList, setWhDataList] = useState([]);

  useEffect(() => {
    fetchOnhand();
    fetchWarehouses();
    fetchEmpMap();
    fetchContractCustomerMap();
    api.get("/app/temp-equip-groups").then((res) => {
      const groups = res.data?.result || [];
      setTempEquipGroupIds(new Set(groups.map((g) => String(g.Group_Id))));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply URL params after data loads
  useEffect(() => {
    if (onhandRows.length === 0) return;
    const urlItemId = searchParams.get("item_id");
    const urlWhId = searchParams.get("wh_id");
    if (urlItemId) {
      if (urlWhId) {
        const found = onhandRows.find(
          (r) => String(r.Item_Id) === urlItemId && String(r.WH_Id) === urlWhId
        );
        if (found) setSelectedRow(found);
      } else {
        const first = onhandRows.find((r) => String(r.Item_Id) === urlItemId);
        if (first) setSelectedRow(first);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onhandRows]);

  const computeHistScrollY = useCallback(() => {
    if (!histContainerRef.current) return;
    const top = histContainerRef.current.getBoundingClientRect().top;
    const y = Math.max(150, Math.floor(window.innerHeight - top - 56 - 39 - 20));
    setHistScrollY(y);
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(computeHistScrollY);
    window.addEventListener("resize", computeHistScrollY);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", computeHistScrollY); };
  }, [computeHistScrollY]);

  useEffect(() => { computeHistScrollY(); }, [computeHistScrollY, selectedRow, histLoading]);


  const fetchOnhand = async () => {
    try {
      const rows = await getDataWithComp("API_1101_WH_ITEM_ONHAND", "ORDER BY Item_Code ASC");
      setOnhandRows(Array.isArray(rows) ? rows : []);
    } catch {
      setOnhandRows([]);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const rows = await getDataWithComp(
        "Tb_Set_WH",
        "AND WH_Status = 'Y' ORDER BY WH_Name ASC"
      );
      setWhDataList(Array.isArray(rows) ? rows : []);
    } catch {
      setWhDataList([]);
    }
  };

  const fetchEmpMap = async () => {
    try {
      const rows = await getDataWithComp(
        "Tb_Set_Employee",
        "AND Emp_Status = 'Y' ORDER BY Emp_Id ASC"
      );
      const map = {};
      (Array.isArray(rows) ? rows : []).forEach((e) => {
        if (e.Emp_Id != null)
          map[Number(e.Emp_Id)] =
            e.Emp_Name || e.Emp_Fullname || `รหัส ${e.Emp_Id}`;
      });
      setEmpMap(map);
    } catch {}
  };

  const fetchContractCustomerMap = async () => {
    try {
      const endpointCandidates = ["/app/admin/temp-equipment", "/app/temp-equipment"];
      let rows = [];
      for (const endpoint of endpointCandidates) {
        try {
          const res = await api.get(endpoint);
          if (res.data?.status === "OK") {
            rows = Array.isArray(res.data?.result) ? res.data.result : [];
            break;
          }
        } catch {
          // try next endpoint
        }
      }
      const map = {};
      rows.forEach((row) => {
        const contractNo = String(row?.contract_no || row?.Contract_No || "").trim();
        if (!contractNo) return;
        const customerName = getContractCustomerName(row);
        if (customerName && customerName !== "-") {
          map[contractNo] = customerName;
        }
      });
      setContractCustomerMap(map);
    } catch {
      setContractCustomerMap({});
    }
  };

  // Fetch history when selected row changes
  useEffect(() => {
    if (!selectedRow?.Item_Id) {
      setHistory([]);
      return;
    }
    const fetch = async () => {
      setHistLoading(true);
      try {
        let query = `AND Item_Id = ${selectedRow.Item_Id} AND WH_Id = ${selectedRow.WH_Id} `;
        const yearAD = histYear ? (parseCurrency(histYear) - 543).toString() : "";
        if (yearAD) query += `AND YEAR(STC_Date) = ${yearAD} `;
        if (histMonth) query += `AND MONTH(STC_Date) = ${parseCurrency(histMonth)} `;
        query += `ORDER BY STC_Date DESC, STC_SEQ DESC`;
        const arr = await getDataWithComp("WH_ITEM_STC", query);
        setHistory(Array.isArray(arr) ? arr : []);
      } catch {
        setHistory([]);
      } finally {
        setHistLoading(false);
      }
    };
    fetch();
  }, [selectedRow?.Item_Id, selectedRow?.WH_Id, histYear, histMonth]);

  useEffect(() => {
    if (!selectedRow?.Item_Id || tempEquipGroupIds.size === 0) { setIsTempEquipItem(false); return; }
    const grp = String(selectedRow?.Item_Group ?? selectedRow?.Item_GroupId ?? "");
    if (grp && tempEquipGroupIds.has(grp)) { setIsTempEquipItem(true); return; }
    getDataWithComp("Tb_Set_Item", `AND Item_Id = ${selectedRow.Item_Id}`, { fields: ["Item_Group"] })
      .then((rows) => {
        const g = String(rows?.[0]?.Item_Group ?? "");
        setIsTempEquipItem(Boolean(g && tempEquipGroupIds.has(g)));
      })
      .catch(() => setIsTempEquipItem(false));
  }, [selectedRow?.Item_Id, tempEquipGroupIds]);

  useEffect(() => {
    if (!selectedRow?.Item_Id) { setItemTotalOnhand(null); return; }
    getDataWithComp("WH_ITEM_Onhand", `AND Item_Id = ${selectedRow.Item_Id}`)
      .then((rows) => {
        const total = (Array.isArray(rows) ? rows : [])
          .reduce((sum, r) => sum + Number(r.Item_Onhand ?? r.Item_OnHand ?? 0), 0);
        setItemTotalOnhand(total);
      })
      .catch(() => setItemTotalOnhand(null));
  }, [selectedRow?.Item_Id]);

  useEffect(() => {
    setHistPage(1);
  }, [histSearch]);

  const handleSelectRow = useCallback(
    (record) => {
      setSelectedRow(record);
      setHistSearch("");
      setHistSearchInput("");
      setHistPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("item_id", String(record.Item_Id));
        next.set("wh_id", String(record.WH_Id));
        return next;
      });
    },
    [setSearchParams]
  );

  const filteredHistory = useMemo(() => {
    if (!histSearch.trim()) return history;
    const kw = histSearch.trim().replace(/\s+/g, " ").toLowerCase();
    return history.filter((item) => {
      const docNo = (item.Doc_No || "").toLowerCase();
      const docNoRef = (item.Doc_NoRef || "").toLowerCase();
      const remark = (item.STC_Remark || "").toLowerCase();
      return docNo.includes(kw) || docNoRef.includes(kw) || remark.includes(kw);
    });
  }, [history, histSearch]);

  /** คงเหลือในคลังหลังรายการนั้น — คำนวณย้อนจากยอดจริงปัจจุบัน (Item_Onhand) ให้สอดคล้องเข้า/ออก แทน STC_Balance ที่อาจบันทึกผิด */
  const reconciledWhBalanceByStcId = useMemo(() => {
    if (!selectedRow || !filteredHistory.length) return null;
    const currentWh = Number(
      selectedRow.Item_Onhand ?? selectedRow.Item_OnHand ?? 0
    );
    if (!Number.isFinite(currentWh)) return null;
    let running = currentWh;
    const map = {};
    for (const r of filteredHistory) {
      if (r.STC_Id == null) continue;
      map[r.STC_Id] = running;
      const qty = Number(r.STC_QTY || 0);
      const doc = r.Doc_Type;
      if (doc === "OUT" || doc === "AO" || doc === "TO") {
        running += qty;
      } else if (
        doc === "IN" ||
        doc === "AI" ||
        doc === "TI" ||
        doc === "BG" ||
        doc === "CC"
      ) {
        running -= qty;
      }
    }
    return map;
  }, [selectedRow, filteredHistory]);

  const pagedHistory = useMemo(() => {
    const start = (histPage - 1) * histPageSize;
    return filteredHistory.slice(start, start + histPageSize).map((item, i) => ({
      ...item,
      __idx: (histPage - 1) * histPageSize + i + 1,
    }));
  }, [filteredHistory, histPage, histPageSize]);

  const itemDetailForAdjust = selectedRow
    ? {
        Item_Id: selectedRow.Item_Id,
        Item_Code: selectedRow.Item_Code || "",
        Item_Name: selectedRow.Item_Name || "",
        STC_Balance:
          selectedRow.Item_Onhand ??
          selectedRow.Item_OnHand ??
          selectedRow.Item_Qty ??
          0,
        WH_Name: selectedRow.WH_Name || "",
      }
    : null;

  // ---- Columns ----
  const historyColumns = useMemo(
    () => [
      { title: "#", width: 50, align: "center", render: (_, __, i) => i + 1 },
      {
        title: "ประเภท", dataIndex: "Doc_Type", width: 90, align: "center",
        render: (v, r) => String(r.Doc_No ?? "").startsWith("CNT") ? "นับสต๊อก" : (v ?? "-"),
      },
      {
        title: "เข้า",
        dataIndex: "STC_QTY",
        width: 80,
        align: "center",
        render: (v, r) => {
          const isCnt = String(r.Doc_No ?? "").startsWith("CNT");
          const isOut = r.Doc_Type === "TO" || r.Doc_Type === "AO" || r.Doc_Type === "OUT";
          if (isCnt) {
            return r.Doc_Type === "AI"
              ? <span style={{ color: "#389e0d" }}>{Number(v || 0).toLocaleString()}</span>
              : "-";
          }
          return isOut ? "-" : <span style={{ color: "#389e0d" }}>{Number(v || 0).toLocaleString()}</span>;
        },
      },
      {
        title: "ออก",
        dataIndex: "STC_QTY",
        width: 80,
        align: "center",
        render: (v, r) => {
          const isCnt = String(r.Doc_No ?? "").startsWith("CNT");
          const isOut = r.Doc_Type === "TO" || r.Doc_Type === "AO" || r.Doc_Type === "OUT";
          if (isCnt) {
            return r.Doc_Type === "AO"
              ? <span style={{ color: "#cf1322" }}>{Number(v || 0).toLocaleString()}</span>
              : "-";
          }
          return isOut ? <span style={{ color: "#cf1322" }}>{Number(v || 0).toLocaleString()}</span> : "-";
        },
      },
      {
        title: "นับได้",
        dataIndex: "STC_Balance",
        width: 80,
        align: "center",
        render: (v, r) => {
          const isCnt = String(r.Doc_No ?? "").startsWith("CNT");
          if (!isCnt) return "-";
          const bal = reconciledWhBalanceByStcId?.[r.STC_Id] ?? Number(r.STC_Balance || 0);
          return <span style={{ color: "#1d4ed8" }}>{Number(bal).toLocaleString()}</span>;
        },
      },
      {
        title: "คงเหลือในคลัง",
        dataIndex: "STC_Balance",
        width: 110,
        align: "center",
        render: (v, r) => {
          const n = reconciledWhBalanceByStcId?.[r.STC_Id];
          const show =
            n !== undefined && n !== null && Number.isFinite(n)
              ? n
              : Number(v || 0);
          return Number(show).toLocaleString();
        },
      },
      {
        title: "วันที่",
        dataIndex: "STC_Date",
        width: 130,
        align: "center",
        render: formatTH,
      },
      {
        title: "เลขเอกสาร", dataIndex: "Doc_No", width: 150, ellipsis: true, align: "left",
        render: (v, r) => {
          const docType = String(r?.Doc_Type || "").toUpperCase();
          if (docType === "IN" || docType === "OUT") {
            const contractNo = String(r?.Doc_NoRef || r?.Doc_No || "").trim();
            const customerName = contractCustomerMap[contractNo] || getContractCustomerName(r);
            const contractIdForJob = Number(r?.Contract_Id);
            if (Number.isFinite(contractIdForJob) && contractIdForJob > 0) {
              const openJobFile = (e) => {
                e.stopPropagation();
                navigate(`/jobfile/${contractIdForJob}`, {
                  state: {
                    fromStockHistory: true,
                    stockOverviewLink: fromPage || "/stock/stock-overview",
                    stockOverviewLabel: fromLabel || "ดูสต๊อกสินค้า",
                    backLink: `${location.pathname}${location.search || ""}`,
                    backLabel: "ประวัติการเคลื่อนไหวสินค้า",
                  },
                });
              };
              return (
                <button
                  type="button"
                  onClick={openJobFile}
                  style={{ color: "#2563eb", textDecoration: "underline", background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  title="เปิดแฟ้มงาน"
                >
                  {customerName}
                </button>
              );
            }
            return customerName;
          }
          if (!v) return "-";
          const prefix = v.slice(0, 3).toUpperCase();
          const isStockDoc = ["ADJ", "TNS", "CNT"].includes(prefix);
          const canOpenStockDocument = fromPage !== "/stock/stock-documents";
          const docTypeMap = { ADJ: "ADJ", TNS: "TR", CNT: "CNT" };
          return (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <CopyOutlined
                style={{ color: "#9ca3af", fontSize: 12, flexShrink: 0, cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(v).then(() => message.success("คัดลอกแล้ว")); }}
              />
              {isStockDoc && canOpenStockDocument ? (
                <span
                  style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/stock/stock-documents", {
                      state: {
                        searchDoc: v,
                        docTypeFilter: docTypeMap[prefix],
                        activeDocNo: v,
                        backLink: location.pathname + location.search,
                        backLabel: "ประวัติการเคลื่อนไหวสินค้า",
                        backState: location.state,
                        fromStockHistory: true,
                      },
                    });
                  }}
                >
                  {v}
                </span>
              ) : v}
            </span>
          );
        },
      },
      {
        title: "เอกสารอ้างอิง",
        dataIndex: "Doc_NoRef",
        width: 130,
        ellipsis: true,
        align: "center",
        render: (v, r) => {
          const docType = String(r?.Doc_Type || "").toUpperCase();
          if (docType === "IN" || docType === "OUT") {
            const contractNo = String(r?.Contract_No || "").trim();
            const contractId = Number(r?.Contract_Id);
            if (contractNo && Number.isFinite(contractId) && contractId > 0) {
              return (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/contract/edit/${contractId}`, {
                      state: {
                        fromStockHistory: true,
                        stockOverviewLink: "/stock/stock-overview",
                        stockOverviewLabel: "ดูสต๊อกสินค้า",
                        stockHistoryBackLink: `${location.pathname}${location.search || ""}`,
                        stockHistoryBackLabel: "ประวัติการเคลื่อนไหวสินค้า",
                        backLink: `${location.pathname}${location.search || ""}`,
                        backLabel: "ประวัติการเคลื่อนไหวสินค้า",
                      },
                    });
                  }}
                  style={{ color: "#2563eb", textDecoration: "underline", background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  title="แก้ไขสัญญา"
                >
                  {contractNo}
                </button>
              );
            }
            return contractNo || "-";
          }
          return v || "-";
        },
      },
      {
        title: "ผู้ทำรายการ",
        dataIndex: "STC_By",
        width: 130,
        align: "center",
        render: (v) =>
          v != null ? empMap[Number(v)] || `รหัส ${v}` : "-",
      },
      {
        title: "หมายเหตุ",
        dataIndex: "STC_Remark",
        width: 180,
        ellipsis: true,
        align: "left",
        render: (v, r) => {
          const docType = String(r?.Doc_Type || "").toUpperCase();
          const roundNo = Number(r?.Service_Round_No);
          const totalRounds = Number(r?.Service_Round_Total);
          const roundNoText = Number.isFinite(roundNo) && roundNo > 0 ? roundNo : 0;
          const totalRoundsText = Number.isFinite(totalRounds) && totalRounds > 0 ? totalRounds : 0;
          const roundSuffix = `ครั้งที่เข้าบริการ ${roundNoText}/${totalRoundsText}`;
          if (docType === "IN") {
            const whName = r?.WH_Name || selectedRow?.WH_Name || "";
            return isTempEquipItem ? `เก็บคืน คลัง${whName} ${roundSuffix}` : `คลัง${whName}`;
          }
          if (docType === "OUT") {
            return isTempEquipItem ? `วางไว้ ${roundSuffix}` : roundSuffix;
          }
          return v || "-";
        },
      },
    ],
    [reconciledWhBalanceByStcId, empMap, contractCustomerMap, navigate, location.pathname, location.search, fromPage, fromLabel, selectedRow?.WH_Name, isTempEquipItem]
  );

  const breadcrumbs = [
    { label: "สต๊อกสินค้า", link: "/stock/stock-overview" },
    ...(fromPage !== "/stock/stock-overview"
      ? [{ label: fromLabel, link: fromPage, state: location.state }]
      : [{ label: "ดูสต๊อกสินค้า", link: fromPage, state: location.state }]),
    { label: "ประวัติการเคลื่อนไหวสินค้า" },
  ];

  return (
    <div className="WarehouseStock">
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="btn btn-toggle-sidebar"
        style={{
          position: "fixed",
          top: "1rem",
          left: isSidebarOpen ? "190px" : "-15px",
          zIndex: 1001,
          background: "transparent",
          border: "none",
          color: "#E13F57",
          fontSize: "20px",
        }}
      >
        {isSidebarOpen ? "✖" : "☰"}
      </button>
      <div className="wrapper">
        <Sidebar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
        <div
          className={`main-panel ${
            isSidebarOpen ? "sidebar-open" : "sidebar-closed"
          }`}
          style={{ height: "100vh", overflow: "hidden" }}
        >
          <Topbar breadcrumbs={breadcrumbs} />
          <div className="container">
            <div className="page-inner" style={{ marginTop: "56px" }}>
              <div
                className="p-3"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: "calc(100vh - 120px)",
                }}
              >
                <div
                  className="bg-white rounded-lg shadow-sm px-4 py-4 md:px-6 md:py-5"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    minHeight: 0,
                  }}
                >
                  <Row
                    justify="space-between"
                    align="middle"
                    gutter={[12, 12]}
                    style={{ flexShrink: 0 }}
                  >
                    <Col>
                      <Space align="center">
                        <Button
                          icon={<ArrowLeftOutlined />}
                          onClick={() => navigate(fromPage, { state: location.state })}
                          style={{ borderColor: "#E13F57", color: "#E13F57" }}
                        >
                          กลับหน้าสินค้า
                        </Button>
                        <Title level={3} style={{ marginBottom: 0 }}>
                          ประวัติการเคลื่อนไหวสินค้า
                        </Title>
                      </Space>
                    </Col>
                  </Row>

                  <Row
                    gutter={[16, 16]}
                    style={{ flex: 1, minHeight: 0, marginTop: 16, alignItems: "stretch" }}
                  >
                    {/* ---- ประวัติสินค้าเคลื่อนไหว ---- */}
                    <Col
                      xs={24}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                      }}
                    >
                      <div
                        className="data-table-wrapper"
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0,
                          overflow: "hidden",
                        }}
                      >
                        {selectedRow ? (
                          <>
                            <div style={{ padding: "12px 16px 12px 16px", flexShrink: 0 }}>
                              {/* บรรทัดชื่อสินค้า + ยอดรวมทุกคลัง (ปัจจุบัน) */}
                              <div
                                style={{
                                  marginBottom: 8,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 8,
                                }}
                              >
                                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  <span style={{ fontWeight: 600, fontSize: 14 }}>
                                    {selectedRow.Item_Name}
                                  </span>
                                  <span style={{ color: "#E13F57", marginLeft: 8, fontWeight: 500, fontSize: 14 }}>
                                    — {selectedRow.WH_Name}
                                  </span>
                                </div>
                                {itemTotalOnhand != null && (
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "#555",
                                      whiteSpace: "nowrap",
                                      flexShrink: 0,
                                    }}
                                  >
                                    รวมทุกคลัง:{" "}
                                    <b style={{ color: "#222" }}>{itemTotalOnhand.toLocaleString()}</b>
                                  </span>
                                )}
                              </div>
                              {/* บรรทัด filter + ปุ่ม */}
                              <Row gutter={[8, 8]} align="middle" wrap={false} style={{ flexWrap: "nowrap" }}>
                                <Col flex="none">
                                  <Space>
                                    <span>ปี:</span>
                                    <Select
                                      style={{ width: 95 }}
                                      value={histYear}
                                      onChange={setHistYear}
                                      options={YEAR_OPTIONS}
                                    />
                                  </Space>
                                </Col>
                                <Col flex="none">
                                  <Space>
                                    <span>เดือน:</span>
                                    <Select
                                      placeholder="ทั้งปี"
                                      style={{ width: 115 }}
                                      value={histMonth || undefined}
                                      onChange={(v) => setHistMonth(v || "")}
                                      options={MONTH_OPTIONS}
                                    />
                                  </Space>
                                </Col>
                                <Col flex="auto">
                                  <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="เลขเอกสาร, หมายเหตุ"
                                    allowClear
                                    value={histSearchInput}
                                    onChange={(e) => setHistSearchInput(e.target.value)}
                                    onPressEnter={() => { setHistSearch(histSearchInput); setHistPage(1); }}
                                    onClear={() => { setHistSearchInput(""); setHistSearch(""); setHistPage(1); }}
                                    style={{ width: "100%", minWidth: 140 }}
                                  />
                                </Col>
                                <Col flex="none">
                                  <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    onClick={() => setAdjustModalOpen(true)}
                                    style={{ background: "#E13F57", borderColor: "#E13F57", whiteSpace: "nowrap" }}
                                  >
                                    ปรับปรุงคลัง
                                  </Button>
                                </Col>
                              </Row>
                            </div>
                            <div ref={histContainerRef} style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                              <Table
                                size="small"
                                columns={historyColumns}
                                dataSource={pagedHistory}
                                rowKey="STC_Id"
                                loading={histLoading}
                                pagination={false}
                                scroll={{ y: histScrollY, x: "max-content" }}
                                onRow={() => ({ style: { whiteSpace: "nowrap" } })}
                              />
                            </div>
                            <div className="data-table-footer">
                              <Pagination
                                totalItems={filteredHistory.length}
                                itemsPerPage={histPageSize}
                                currentPage={histPage}
                                compact
                                onPageChange={setHistPage}
                                onItemsPerPageChange={(val) => {
                                  setHistPage(1);
                                  setHistPageSize(Number(val));
                                }}
                              />
                            </div>
                          </>
                        ) : (
                          <p
                            style={{
                              padding: "40px 24px",
                              color: "#999",
                              fontSize: 14,
                              textAlign: "center",
                              margin: 0,
                            }}
                          >
                            กรุณาเลือกสินค้าจากหน้าสต๊อกสินค้าก่อน
                          </p>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AdjustWhModal
        whDataList={whDataList || []}
        showWhModal={adjustModalOpen}
        handleWhClose={() => setAdjustModalOpen(false)}
        handleADJClose={async () => {
          setAdjustModalOpen(false);
          await fetchOnhand();
        }}
        itemDetailModal={itemDetailForAdjust}
        warehouseId={selectedRow?.WH_Id ?? null}
      />
    </div>
  );
}
